import type React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type MarkdownSize = 'xs' | 'sm' | 'md' | 'lg';
export type MarkdownVariant = 'default' | 'muted' | 'accent';

export interface MarkdownTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: string;
  size?: MarkdownSize;
  variant?: MarkdownVariant;
}

export function MarkdownText({ 
  children, 
  size = 'sm', 
  variant = 'default',
  className = '',
  ...props 
}: MarkdownTextProps) {
  // Size-based styling
  const sizeClasses = {
    xs: {
      prose: 'prose-xs',
      h1: 'text-base font-bold mb-1.5',
      h2: 'text-sm font-semibold mb-1.5',
      h3: 'text-xs font-semibold mb-1',
      p: 'mb-1.5 text-xs',
      code: 'text-xs'
    },
    sm: {
      prose: 'prose-sm',
      h1: 'text-lg font-bold mb-2',
      h2: 'text-base font-semibold mb-2',
      h3: 'text-sm font-semibold mb-1',
      p: 'mb-2 text-sm',
      code: 'text-xs'
    },
    md: {
      prose: 'prose',
      h1: 'text-xl font-bold mb-3',
      h2: 'text-lg font-semibold mb-2',
      h3: 'text-base font-semibold mb-2',
      p: 'mb-3 text-base',
      code: 'text-sm'
    },
    lg: {
      prose: 'prose-lg',
      h1: 'text-2xl font-bold mb-4',
      h2: 'text-xl font-semibold mb-3',
      h3: 'text-lg font-semibold mb-2',
      p: 'mb-4 text-lg',
      code: 'text-base'
    }
  };

  // Variant-based colors
  const variantClasses = {
    default: {
      text: 'text-text-primary',
      muted: 'text-text-muted',
      accent: 'text-text-accent',
      accentHover: 'hover:text-text-accent-hover'
    },
    muted: {
      text: 'text-text-muted',
      muted: 'text-text-disabled',
      accent: 'text-text-secondary',
      accentHover: 'hover:text-text-primary'
    },
    accent: {
      text: 'text-text-accent',
      muted: 'text-text-secondary',
      accent: 'text-action-key-default',
      accentHover: 'hover:text-action-key-darker'
    }
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  return (
    <div className={`${currentSize.prose} max-w-none font-manrope ${className}`} {...props}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className={`${currentSize.p} ${currentVariant.text} last:mb-0`}>{children}</p>,
          h1: ({ children }) => <h1 className={`${currentSize.h1} ${currentVariant.text}`}>{children}</h1>,
          h2: ({ children }) => <h2 className={`${currentSize.h2} ${currentVariant.text}`}>{children}</h2>,
          h3: ({ children }) => <h3 className={`${currentSize.h3} ${currentVariant.text}`}>{children}</h3>,
          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className={currentVariant.text}>{children}</li>,
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className={`bg-neutral-fill-lighter ${currentVariant.text} px-1.5 py-0.5 rounded-md ${currentSize.code} font-mono`}>
                {children}
              </code>
            ) : (
              <code className={`block bg-neutral-fill-lighter ${currentVariant.text} p-3 rounded-lg ${currentSize.code} font-mono whitespace-pre overflow-x-auto border border-border-light`}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => <div className="mb-3">{children}</div>,
          blockquote: ({ children }) => (
            <blockquote className={`border-l-4 border-border-medium pl-4 italic ${currentVariant.muted} mb-3`}>
              {children}
            </blockquote>
          ),
          strong: ({ children }) => <strong className={`font-semibold ${currentVariant.text}`}>{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ children, href }) => (
            <a 
              href={href} 
              className={`${currentVariant.accent} ${currentVariant.accentHover} underline transition-colors`}
              target="_blank" 
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-border-medium rounded-lg overflow-hidden">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-bg-surface">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-bg-secondary divide-y divide-border-light">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-bg-surface-hover transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className={`px-4 py-3 text-left ${currentSize.code} font-semibold ${currentVariant.muted} uppercase tracking-wider border-b border-border-medium`}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={`px-4 py-3 ${currentSize.code} ${currentVariant.text} border-b border-border-light`}>
              {children}
            </td>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}